import { Overlay } from "@brexhq/component-library";
import styled from "@emotion/styled";
import React from "react";

import { Button } from "@brexhq/next/src/components/shared/Button";
import type { VideoComponentProps } from "@brexhq/next/src/components/shared/Video/types";
import { Video } from "@brexhq/next/src/components/shared/Video/video";
import { breakpointValues } from "@brexhq/shared/styles/breakpoints";
export interface VideoModalProps extends VideoComponentProps {
  isOpen: boolean;
  onClose: () => void;
  programName?: string;
  lightMode?: boolean;
  maxWidth?: string;
  placeholderWidth?: number;
  sidesPadding?: number;
}

const MaxWidth = styled("div")<{ sidesPadding: number }>(
  ({ theme, sidesPadding }) => ({
    width: "100%",
    maxWidth: theme.grid.maxContentWidth,
    zIndex: 2,
    position: "relative",
    padding: `0 ${sidesPadding}px`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  }),
);

const BackButtonContainer = styled("div")<{ maxWidth: string }>(
  ({ maxWidth }) => ({
    width: "100%",
    display: "flex",
    justifyContent: "flex-start",
    [`@media (min-width: ${breakpointValues.SML}px)`]: {
      maxWidth: maxWidth, // Apply maxWidth only for non-mobile screens
    },
  }),
);

const FullHeightVideo = styled(Video, {
  shouldForwardProp: (prop) => prop !== "width" && prop !== "maxWidth",
})(({ width, maxWidth }) => ({
  width: width ?? "100%",
  maxHeight: "calc(100vh - 200px)",
  borderRadius: "12px",
  [`@media (min-width: ${breakpointValues.SML}px)`]: {
    maxWidth: maxWidth, // Apply maxWidth only for non-mobile screens
  },
}));

export const VideoModal: React.FC<VideoModalProps> = ({
  isOpen,
  src,
  posterImageSrc,
  onClose,
  captionFile,
  programName,
  lightMode = false,
  maxWidth = "100%",
  sidesPadding,
}) => {
  const [placeholderWidth, setPlaceholderWidth] = React.useState(0);

  React.useEffect(() => {
    const calculatePlaceholderWidth = () => {
      const innerWidth = window.innerWidth;
      const maxWidthInt = parseInt(maxWidth.replace("%", ""));
      const placeholderWidthInPx =
        (maxWidthInt / 100) * innerWidth - (sidesPadding ?? 20) * 2;
      setPlaceholderWidth(placeholderWidthInPx);
    };

    calculatePlaceholderWidth();
    window.addEventListener("resize", calculatePlaceholderWidth);

    return () => {
      window.removeEventListener("resize", calculatePlaceholderWidth);
    };
  }, [maxWidth, sidesPadding]);

  // Splitting this into two components so we dont have event listeners and refs etc when not necessary
  if (!isOpen) return null;
  return (
    <Modal
      onClose={onClose}
      posterImageSrc={posterImageSrc}
      src={src}
      captionFile={captionFile}
      programName={programName}
      lightMode={lightMode}
      maxWidth={maxWidth}
      placeholderWidth={placeholderWidth}
      sidesPadding={sidesPadding}
    />
  );
};

const Modal: React.FC<Omit<VideoModalProps, "isOpen">> = ({
  onClose,
  src,
  posterImageSrc,
  captionFile,
  programName,
  lightMode,
  maxWidth,
  placeholderWidth,
  sidesPadding = 20,
}) => {
  return (
    <Overlay
      background="darken"
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      onClose={onClose}
      testId="videoOverlay"
    >
      <MaxWidth sidesPadding={sidesPadding}>
        <BackButtonContainer maxWidth={maxWidth ?? "100%"}>
          <Button
            size={{
              LRG: "lrg",
              MED: "lrg",
              SML: "med",
            }}
            testId="closeVideoModal"
            component="VideoModel"
            label="Back to page"
            modifier={"outline"}
            onClick={onClose}
            theme={lightMode ? "PrimaryWhite" : "PrimaryInk"}
          />
        </BackButtonContainer>
        <FullHeightVideo
          controls
          autoPlay
          muted={false}
          src={src}
          posterImageSrc={posterImageSrc}
          captionFile={captionFile}
          programName={programName}
          width={placeholderWidth}
          maxWidth={maxWidth}
        />
      </MaxWidth>

      <div
        style={{
          background: lightMode
            ? "rgba(235, 235, 241, 0.5)"
            : "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1,
        }}
      />
    </Overlay>
  );
};
